import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, MagnifyingGlass, CheckCircle, X, Tag, CaretDown, Eye } from '@phosphor-icons/react';
import FlagIcons from '../components/FlagIcons';
import AssignMBQuickPicker from '../components/AssignMBQuickPicker';
import { useMBList } from '../hooks/useMBList';
import socket, { joinRoom } from '../utils/socket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LONG_PRESS_MS = 400;

const chatTypeColor = (t) => {
  switch (t) {
    case 'deposit': return 'bg-emerald-100 text-emerald-700';
    case 'withdrawal': return 'bg-rose-100 text-rose-700';
    case 'support': return 'bg-blue-100 text-blue-700';
    case 'inquiry': return 'bg-amber-100 text-amber-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(/[Zz]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' })
      : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
  } catch { return ''; }
};

const NewMembersPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const MB_OPTIONS = useMBList();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  // Selection state for bulk-assign
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [mbMenuOpen, setMbMenuOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [previewFor, setPreviewFor] = useState(null);
  const mbMenuRef = useRef(null);
  const longPressTimerRef = useRef(null);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/chats/new-members`, {
        params: { user_id: user?.id, limit: 500 },
      });
      setChats(res.data?.chats || []);
    } catch (_) { /* transient */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user?.id) return;
    load();

    // Build-spec (New Members instant): join the dedicated `new_members` socket
    // room and refresh the moment the server pushes a `new_member_signup` event,
    // so a brand-new signup appears immediately instead of waiting up to ~10s.
    const doJoin = () => joinRoom('new_members', user.id);
    doJoin();
    // Re-join on every (re)connect so a socket reconnect / StrictMode remount
    // never drops the subscription (socket.js also auto-rejoins activeRooms).
    socket.on('connect', doJoin);
    let _rtTimer = null;
    const onNewSignup = () => {
      if (_rtTimer) return;            // coalesce bursts into one fetch / 300ms
      _rtTimer = setTimeout(() => { _rtTimer = null; load(); }, 300);
    };
    socket.on('new_member_signup', onNewSignup);

    // Safety-net poll only (was the primary mechanism; real-time now handles it).
    const t = setInterval(load, 10000);
    return () => {
      clearInterval(t);
      if (_rtTimer) clearTimeout(_rtTimer);
      socket.off('new_member_signup', onNewSignup);
      socket.off('connect', doJoin);
      // Note: intentionally NOT leaving `new_members` — it's a lightweight
      // broadcast room; keeping the subscription avoids a StrictMode
      // join→leave→join race, and with the listener removed nothing fires.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    const onClick = (e) => { if (mbMenuRef.current && !mbMenuRef.current.contains(e.target)) setMbMenuOpen(false); };
    if (mbMenuOpen) document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, [mbMenuOpen]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return chats;
    return chats.filter((c) => {
      return [c.customer_name, c.customer_phone, c.customer_client_id, c.chat_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [chats, query]);

  const openChat = (c) => {
    // New Customer Flow: a brand-new signup may have NO chat_room yet. Don't
    // navigate to a broken /chat/null — instead drop into select mode so the
    // handler/admin can assign an MB right away (the primary N action).
    if (!c.room_id) {
      if (!selectMode) setSelectMode(true);
      toggleSelect(c.customer_id);
      return;
    }
    const path = user?.role === 'super_admin' ? `/admin-chat/${c.room_id}` : `/handler-chat/${c.room_id}`;
    navigate(path, { state: { fromNewMembers: true } });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startLongPress = (id) => {
    clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      if (!selectMode) setSelectMode(true);
      toggleSelect(id);
      // subtle haptic hint on mobile
      try { if (navigator.vibrate) navigator.vibrate(20); } catch (_) {}
    }, LONG_PRESS_MS);
  };
  const cancelLongPress = () => { clearTimeout(longPressTimerRef.current); };

  const handleRowClick = (c) => {
    if (selectMode) {
      toggleSelect(c.customer_id);
    } else {
      openChat(c);
    }
  };

  // Item 3 — Eye preview: jump straight into a specific chat-type room.
  const gotoRoom = (room_id) => {
    if (!room_id) return;
    const path = user?.role === 'super_admin' ? `/admin-chat/${room_id}` : `/handler-chat/${room_id}`;
    navigate(path, { state: { fromNewMembers: true } });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setMbMenuOpen(false);
  };

  const selectAll = () => {
    setSelectedIds(new Set(filtered.map((c) => c.customer_id)));
  };

  const bulkAssign = async (mb) => {
    if (selectedIds.size === 0) { toast.error('Select at least one customer'); return; }
    setBulkBusy(true);
    setMbMenuOpen(false);
    const ids = Array.from(selectedIds);
    // Snapshot for undo — every affected customer's previous MB (all null here
    // since N-list only lists unassigned customers, but future-proof anyway).
    const previousById = new Map();
    ids.forEach((id) => {
      const row = chats.find((c) => c.customer_id === id);
      previousById.set(id, row?.mb_setup || null);
    });
    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          axios.patch(`${API}/clients/${id}/mb-setup`, { mb_setup: mb, actor_id: user?.id })
        )
      );
      const okIds = ids.filter((_, i) => results[i].status === 'fulfilled');
      const failed = ids.length - okIds.length;
      if (okIds.length === 0) {
        toast.error('All assignments failed');
      } else {
        // 5-second undo — reverts every successfully assigned customer.
        const undo = async () => {
          try {
            await Promise.allSettled(okIds.map((id) =>
              axios.patch(`${API}/clients/${id}/mb-setup`, {
                mb_setup: previousById.get(id) || null,
                actor_id: user?.id,
              })
            ));
            toast.success(`Undone — ${okIds.length} customer${okIds.length === 1 ? '' : 's'} reverted`);
            load();
          } catch (_) { toast.error('Undo failed'); }
        };
        toast.success(
          `Assigned ${okIds.length} to ${mb}${failed ? ` (${failed} failed)` : ''}`,
          { duration: 5000, action: { label: 'Undo', onClick: undo } }
        );
      }
      exitSelectMode();
      load();
    } catch (_) {
      toast.error('Bulk assign failed');
    } finally { setBulkBusy(false); }
  };

  const bulkClear = async () => {
    if (selectedIds.size === 0) { toast.error('Select at least one to clear'); return; }
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    // Optimistically drop the cleared rows from the list.
    const removedSet = new Set(ids);
    setChats((prev) => prev.filter((c) => !removedSet.has(c.customer_id)));
    try {
      const res = await axios.post(`${API}/chats/new-members/clear`, {
        customer_ids: ids,
        actor_id: user?.id,
      });
      toast.success(`Cleared ${res.data?.cleared ?? ids.length} from New Members`);
      exitSelectMode();
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Clear failed';
      toast.error(typeof msg === 'string' ? msg : 'Clear failed');
      load(); // revert optimistic removal on failure
    } finally { setBulkBusy(false); }
  };

  const isAdmin = user?.role === 'super_admin';
  const selectedCount = selectedIds.size;

  return (
    <div className="h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#008069] text-white p-3 flex items-center gap-3 sticky top-0 z-20 shadow">
        {selectMode ? (
          <>
            <button
              onClick={exitSelectMode}
              className="p-1.5 hover:bg-white/15 rounded-full"
              data-testid="new-members-exit-select"
              title="Cancel selection"
            >
              <X size={22} weight="bold" />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-base font-bold truncate">
                {selectedCount} selected
              </div>
              <button
                onClick={selectAll}
                className="text-[11px] text-white/80 underline"
                data-testid="new-members-select-all"
              >
                Select all {filtered.length}
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 hover:bg-white/15 rounded-full"
              data-testid="new-members-back-btn"
            >
              <ArrowLeft size={22} weight="bold" />
            </button>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-full bg-sky-500 flex items-center justify-center font-black text-base">N</div>
              <div className="min-w-0">
                <h1 className="text-base font-bold truncate">New Members</h1>
                <p className="text-[11px] text-white/70 truncate">
                  {chats.length} customer{chats.length === 1 ? '' : 's'} · long-press or Select to bulk-assign
                </p>
              </div>
            </div>
            {chats.length > 0 && (
              <button
                onClick={() => setSelectMode(true)}
                className="text-[12px] font-bold px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/30"
                data-testid="new-members-enter-select"
              >
                Select
              </button>
            )}
          </>
        )}
      </div>

      {/* Search */}
      {!selectMode && (
        <div className="p-3 bg-white border-b border-slate-200 flex-shrink-0">
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="Search name, phone, Client ID…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm bg-slate-100 border border-slate-200 rounded-full focus:outline-none focus:border-sky-400 focus:bg-white"
              data-testid="new-members-search"
            />
          </div>
        </div>
      )}

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto pb-24" data-testid="new-members-list">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3 text-sky-600 font-black text-xl">N</div>
            <p className="text-sm font-semibold text-slate-700">All caught up ✓</p>
            <p className="text-xs text-slate-500 mt-1">
              {query ? 'No matches for that search.' : 'Every customer has an MB assigned.'}
            </p>
          </div>
        ) : (
          <ul>
            {filtered.map((c) => {
              const selected = selectedIds.has(c.customer_id);
              return (
                <li key={c.customer_id} className="relative">
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(c)}
                    onMouseDown={() => startLongPress(c.customer_id)}
                    onMouseUp={cancelLongPress}
                    onMouseLeave={cancelLongPress}
                    onTouchStart={() => startLongPress(c.customer_id)}
                    onTouchEnd={cancelLongPress}
                    onTouchMove={cancelLongPress}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      if (!selectMode) setSelectMode(true);
                      toggleSelect(c.customer_id);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 border-b border-slate-100 text-left transition-colors cursor-pointer ${selected ? 'bg-sky-100/70 hover:bg-sky-100' : 'hover:bg-sky-50/60'}`}
                    data-testid={`new-member-item-${c.customer_id}`}
                  >
                    <div className="relative flex-shrink-0">
                      {selectMode ? (
                        <div className={`w-11 h-11 rounded-full flex items-center justify-center border-2 ${selected ? 'bg-sky-500 border-sky-600' : 'bg-white border-slate-300'}`}>
                          {selected ? (
                            <CheckCircle size={26} weight="fill" className="text-white" />
                          ) : (
                            <span className="text-slate-500 font-bold">{(c.customer_name || '?').charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                      ) : c.customer_profile_pic ? (
                        <img src={c.customer_profile_pic} alt="" className="w-11 h-11 rounded-full object-cover" />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-sky-500 text-white flex items-center justify-center font-bold text-base">
                          {(c.customer_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      {!selectMode && c.unread_count > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
                          {c.unread_count > 9 ? '9+' : c.unread_count}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-semibold text-slate-800 truncate">{c.customer_name}</span>
                        {c.customer_flag && <FlagIcons flag={c.customer_flag} size={11} />}
                        {c.customer_client_id && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 uppercase truncate max-w-[100px]">
                            {c.customer_client_id}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                        {(c.chat_types || [c.chat_type]).map((t) => (
                          <span
                            key={t}
                            className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase ${chatTypeColor(t)}`}
                            data-testid={`new-member-chat-type-${c.customer_id}-${t}`}
                          >
                            {t}
                          </span>
                        ))}
                        <span className="text-[11px] text-slate-500 truncate">
                          {c.last_message || (c.customer_phone ? `📞 ${c.customer_phone}` : 'No messages yet')}
                        </span>
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      <div className="text-right">
                        <div className="text-[10px] text-slate-400">{fmtTime(c.last_message_time)}</div>
                        <div className="text-[9px] text-orange-600 font-bold mt-0.5">NO MB</div>
                      </div>
                      {!selectMode && (
                        <>
                          {/* Point 10 — per-row hand icon (MB assign), immediate
                              dropdown via Portal (no scroll needed). Only real
                              customers with no MB (New Users excluded). */}
                          {!c.is_new_user && c.customer_id && !c.mb_setup && (
                            <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                              <AssignMBQuickPicker
                                customerId={c.customer_id}
                                currentMB={null}
                                actorId={user?.id}
                                actorRole={user?.role}
                                compact
                                onAssigned={() => load()}
                              />
                            </div>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const hasRooms = (c.rooms || []).some((r) => r.room_id);
                              if (!hasRooms) return; // Point 10 — no chat → nothing happens
                              setPreviewFor(previewFor === c.customer_id ? null : c.customer_id);
                            }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${(c.rooms || []).some((r) => r.room_id) ? 'bg-sky-100 hover:bg-sky-200 text-sky-700' : 'bg-slate-100 text-slate-300 cursor-not-allowed'}`}
                            data-testid={`new-member-eye-${c.customer_id}`}
                            title={(c.rooms || []).some((r) => r.room_id) ? "Preview this customer's chats" : 'No chat yet'}
                          >
                            <Eye size={16} weight="bold" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  {previewFor === c.customer_id && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setPreviewFor(null)} />
                      <div
                        className="absolute right-4 top-16 z-30 bg-white rounded-xl shadow-2xl border border-slate-200 w-64 max-h-[50vh] overflow-y-auto"
                        data-testid={`new-member-preview-${c.customer_id}`}
                      >
                        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 flex items-center justify-between">
                          <span>Chats · {c.customer_name}</span>
                          <button onClick={() => setPreviewFor(null)}><X size={12} weight="bold" /></button>
                        </div>
                        {(c.rooms || []).filter((r) => r.room_id).length === 0 ? (
                          <div className="px-3 py-6 text-center text-xs text-slate-400" data-testid={`new-member-preview-empty-${c.customer_id}`}>No chat found</div>
                        ) : (
                          (c.rooms || []).filter((r) => r.room_id).map((r) => (
                            <button
                              key={r.room_id}
                              onClick={() => { setPreviewFor(null); gotoRoom(r.room_id); }}
                              className="w-full flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-sky-50 text-left"
                              data-testid={`new-member-preview-row-${c.customer_id}-${r.chat_type}`}
                            >
                              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase flex-shrink-0 ${chatTypeColor(r.chat_type)}`}>{r.chat_type}</span>
                              <span className="flex-1 min-w-0 text-[12px] text-slate-600 truncate">{r.last_message || '(opened, no message)'}</span>
                              {r.unread_count > 0 && (
                                <span className="min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center flex-shrink-0">{r.unread_count}</span>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* Bulk-assign floating toolbar */}
      {selectMode && (
        <div className="fixed bottom-4 inset-x-0 flex justify-center z-30" data-testid="bulk-assign-toolbar">
          <div className="bg-slate-900 text-white rounded-full shadow-2xl border border-slate-700 flex items-center gap-3 px-4 py-2.5">
            <span className="text-[13px] font-semibold">
              {selectedCount} selected
            </span>
            <div className="w-px h-5 bg-slate-600" />
            {isAdmin && (
              <button
                onClick={bulkClear}
                disabled={selectedCount === 0 || bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-[12px] font-bold disabled:opacity-50"
                data-testid="bulk-clear-btn"
              >
                <X size={13} weight="bold" />
                Clear
              </button>
            )}
            <div className="relative" ref={mbMenuRef}>
              <button
                onClick={(e) => { e.stopPropagation(); setMbMenuOpen((v) => !v); }}
                disabled={selectedCount === 0 || bulkBusy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-[12px] font-bold disabled:opacity-50"
                data-testid="bulk-assign-mb-btn"
              >
                <Tag size={13} weight="bold" />
                Assign MB
                <CaretDown size={11} weight="bold" />
              </button>
              {mbMenuOpen && createPortal(
                <div
                  className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
                  style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 16px)', paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
                  onClick={(e) => { e.stopPropagation(); setMbMenuOpen(false); }}
                  data-testid="bulk-assign-mb-overlay"
                >
                  <div
                    className="bg-white text-slate-800 rounded-2xl shadow-2xl border border-slate-200 w-[240px] max-w-[86vw] max-h-[80vh] overflow-y-auto py-1"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="bulk-assign-mb-menu"
                  >
                    <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wide text-slate-500 border-b border-slate-100 mb-0.5 sticky top-0 bg-white flex items-center justify-between">
                      <span>Assign selected to</span>
                      <button onClick={(e) => { e.stopPropagation(); setMbMenuOpen(false); }} className="text-slate-400"><X size={14} weight="bold" /></button>
                    </div>
                    {MB_OPTIONS.map((mb) => (
                      <button
                        key={mb}
                        onClick={() => bulkAssign(mb)}
                        disabled={bulkBusy}
                        className="w-full text-left px-4 py-2.5 text-[14px] hover:bg-amber-50 text-slate-800 disabled:opacity-50 border-b border-slate-50 last:border-b-0"
                        data-testid={`bulk-assign-opt-${mb}`}
                      >
                        {mb}
                      </button>
                    ))}
                  </div>
                </div>,
                document.body
              )}
            </div>
            <button
              onClick={exitSelectMode}
              className="p-1.5 hover:bg-slate-700 rounded-full"
              data-testid="bulk-assign-cancel"
              title="Cancel"
            >
              <X size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewMembersPage;
